import * as React from 'react'

import { ModalRenderer } from '@thg-commerce/enterprise-modal'
import { ModalRendererProps } from '@thg-commerce/enterprise-modal/src/ModalRenderer/ModalRenderer'
import { useTheme } from '@thg-commerce/enterprise-pages/src/Product/theme'
import { SafeHtml } from '@thg-commerce/gravity-elements'
import {
  margin,
  spacing,
  styled,
  Text,
  TextStyling,
} from '@thg-commerce/gravity-theme'

const Title = styled.h1<{ textStyle?: TextStyling }>`
  ${(props) =>
    props.textStyle
      ? Text(props.textStyle.entry, props.textStyle.style)
      : Text('medium1', 'alternate')}
  margin-bottom: ${spacing(2)};
  text-transform: ${(props) => props.textStyle?.transform || 'none'};
`

const Content = styled(SafeHtml)<{
  backgroundColor?: string
  color?: string
  tableStyle?
}>`
  margin-bottom: ${spacing(2)};
  display: block;
  width: 100%;
  overflow-x: scroll;

  p {
    position: sticky;
    left: 0;
  }

  table caption {
    ${(props) =>
      props.tableStyle?.caption
        ? Text(
            props.tableStyle.caption.font.entry,
            props.tableStyle.caption.font.style,
          )
        : Text('bodyText', 'alternate')}
    text-transform: ${(props) =>
      props.tableStyle?.caption?.font.transform || 'none'};

    margin-bottom: ${spacing(2)};
    text-align: left;
    display: table-caption;

    strong {
      position: sticky;
      left: 0;
    }
  }

  &:focus-visible {
    outline: none;
    border: 2px solid ${(props) => props.theme.colors.palette.brand.base};
  }

  table {
    width: 100%;
    display: table;
    margin-top: ${(props) =>
      props.tableStyle?.margin?.top || props.tableStyle?.margin?.top === 0
        ? props.tableStyle?.margin.top
        : spacing(2)};
    border-collapse: separate;
    margin-bottom: ${spacing(2)};
    outline: 0;
    border: 1px solid ${(props) => props.theme.colors.palette.greys.light};

    &:focus-visible {
      border: 2px solid ${(props) => props.theme.colors.palette.brand.base};
    }

    th,
    td {
      text-align: center;
    }

    thead {
      th {
        border-top: 1px solid
          ${(props) => props.theme.colors.palette.greys.light};
      }
      th:first-child {
        border-left: 1px solid
          ${(props) => props.theme.colors.palette.greys.light};
        position: sticky;
        left: 0;
      }
    }

    th {
      display: table-cell;
      vertical-align: middle;
      min-width: 0;
      padding: ${spacing(1)};
      border-style: none;
      font-weight: bold;
      border-right: 1px solid
        ${(props) => props.theme.colors.palette.greys.light};
      background-color: ${(props) => props.backgroundColor || 'inherit'};
      color: ${(props) => props.color || 'inherit'};
    }

    tbody {
      width: 100%;
      border: 1px solid ${(props) => props.theme.colors.palette.greys.light};
      border-collapse: separate;
      overflow-x: scroll;

      th:first-child {
        position: sticky;
        left: 0;
        border-right: 1px solid
          ${(props) => props.theme.colors.palette.greys.light};
      }
      th:last-child {
        border-right: 0;
      }
    }

    tbody:focus-visible {
      border: 2px solid ${(props) => props.theme.colors.palette.brand.base};
      outline: 0;
    }
    tr {
      display: table-row;
    }
    tr:first-child td {
      ${Text('bodyText', 'alternate')};
    }
    tr:first-child th {
      border-bottom: 1px solid
        ${(props) => props.theme.colors.palette.greys.light};
    }
    tr:first-child th {
      border-bottom: 1px solid
        ${(props) => props.theme.colors.palette.greys.light};
    }

    td:first-child {
      ${Text('bodyText', 'alternate')};
      position: sticky;
      left: 0;
      border-right: 1px solid
        ${(props) => props.theme.colors.palette.greys.light};
      background-color: inherit;
    }

    td:last-child {
      border-right: 0;
    }

    td {
      padding: ${spacing(2)};
      border: 0px;
      border-right: 1px solid
        ${(props) => props.theme.colors.palette.greys.light};
    }
  }
`

export interface SizeGuideModalProps {
  i18nText: {
    closeI18nText: ModalRendererProps['closeI18nText']
    title: string
  }
  content: string
  onClose: () => void
}

export const SizeGuideModal = (props: SizeGuideModalProps) => {
  const theme = useTheme()
  return (
    <ModalRenderer
      closeI18nText={props.i18nText.closeI18nText}
      gridColSpan={theme.pageTheme.sizeGuide.modal?.width || [12, 12, 10, 6]}
      stickyHeader={true}
      showHeader={true}
      onClose={props.onClose}
      data-testid="size-guide-modal"
      content={{ focusable: true }}
      margin={theme.pageTheme.sizeGuide.modal?.margin}
      modalHeight={theme.pageTheme.sizeGuide.modal?.height}
      renderContent={() => {
        return (
          <React.Fragment>
            <Title textStyle={theme.pageTheme.sizeGuide.title}>
              {props.i18nText.title}
            </Title>
            <Content
              content={props.content}
              backgroundColor={
                theme.pageTheme.sizeGuide.table?.head?.backgroundColor
              }
              tableStyle={theme.pageTheme.sizeGuide?.table}
              color={theme.pageTheme.sizeGuide.table?.head?.color}
              onTag={(tag: string, html: string) => {
                if (tag === 'table' && html.match(/<table/)) {
                  const table = html.split('<table')
                  return `<table tabindex="0" ${table[1]}`
                }
                return null
              }}
              __dangerouslyAllowedAttrs={{
                tbody: ['tabindex'],
                td: ['colspan'],
              }}
            />
          </React.Fragment>
        )
      }}
    />
  )
}
